import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { IconAlertCircle, IconAlertTriangle, IconArrowRight, IconCalendarDollar, IconChevronLeft, IconChevronRight, IconCreditCard, IconDots, IconDownload, IconExternalLink, IconInfoCircle, IconLoader2, IconPlus, IconReceipt, IconRefresh, IconRosetteDiscountCheck, IconSettings, IconWorld, IconX } from '@tabler/icons-react'
import { MASCOT_OUTLINE_PATH } from '@webbuilder/ui'
import { SectionLoader, useRecordLoader } from '../RecordLoader'
import type { Translate } from '@webbuilder/i18n'
import { activeLanguage, useI18n } from '../../lib/i18n'
import { moneyLocale } from '../../lib/region'
import { DashboardPageHeader } from '../DashboardPageHeader'
import { CancelMembershipSummary } from '../CancelMembershipSummary'
import { ConfirmDialog } from '../ProjectWorkspace/ConfirmDialog'
import { CardMark } from '../ProjectWorkspace/sections/CardMark'
import { AddCardDialog } from './AddCardDialog'
import { errorMessage } from '../ProjectWorkspace/sections/offerShared'
import {
  getBillingOverview, listBillingInvoices, cancelMembership, resumeMembership,
  setDefaultCard, removeCard, openBillingPortal, deleteProjectDomain,
  type BillingOverview, type BillingCard, type BillingMembership,
  type BillingDomainCharge, type BillingInvoicePage,
} from '../../lib/api'

/**
 * Facturación: todo lo que se le cobra a este usuario.
 *
 * Y sólo lo que se le cobra de verdad. Aquí hubo un tercer bloque de planes de
 * "workspace" que no existe como producto: se pintaba un plan gratis, un costo
 * mensual y unas facturas que nadie había emitido. Una pantalla de dinero
 * inventado es peor que una vacía, porque no se distingue de una real.
 *
 * Quedan las dos cosas que sí se cobran —la membresía y los dominios— y las dos
 * que las sostienen: las tarjetas guardadas y el historial de facturas. Todo
 * sale de `/api/billing/overview`, que lo lee de Stripe en el momento.
 *
 * La pantalla se lee de arriba abajo como se responde a una pregunta de dinero:
 * qué pasa ahora (avisos), en qué estoy (resumen), qué me van a cobrar y con
 * qué (próximos cobros y tarjetas), y qué me han cobrado (historial). Nada de
 * lo que se muestra se calcula de más: lo derivado —el estimado mensual, los
 * días que faltan, el vencimiento de una tarjeta— se dice que es derivado.
 */

const PLAN_NAME: Record<BillingMembership['plan'], string> = { free: 'Starter', pro: 'Pro', business: 'Business' }

/** Los estados de Stripe que el usuario tiene que entender sin saber inglés. */
const STATUS_NOTE_KEY: Record<string, string> = {
  past_due: 'billing.status.past_due',
  unpaid: 'billing.status.unpaid',
  incomplete: 'billing.status.incomplete',
  paused: 'billing.status.paused',
}

/* Cómo se lee el estado de un dominio en una pantalla de dinero. `error` es el
   caso que importa: se cobró y el registro no salió, y quien mira sus cobros
   tiene que verlo aquí y no sólo en los ajustes del proyecto. */

const INVOICE_STATUS: Record<string, { label: string; tone: 'ok' | 'warn' | 'muted' }> = {
  paid: { label: 'billing.invoice.paid', tone: 'ok' },
  open: { label: 'billing.invoice.open', tone: 'warn' },
  uncollectible: { label: 'billing.invoice.uncollectible', tone: 'warn' },
  void: { label: 'billing.invoice.void', tone: 'muted' },
}

function money(cents: number | null | undefined, currency = 'usd'): string {
  if (cents === null || cents === undefined) return '—'
  return new Intl.NumberFormat(moneyLocale(), { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100)
}

function shortDate(iso: string | null): string {
  if (!iso) return '—'
  /* El idioma de quien mira, no el de la región: esto es una fecha del panel.
     El `'es-MX'` que había aquí escribía «29 sept 2026» en un panel inglés. */
  return new Date(iso).toLocaleDateString(activeLanguage(), { day: 'numeric', month: 'short', year: 'numeric' })
}

/**
 * Cuánto falta, en palabras.
 *
 * Una fecha sola obliga a restar mentalmente para saber si hay que hacer algo
 * hoy. El «en 3 días» es lo que se lee primero; la fecha queda al lado para
 * quien la necesite exacta.
 */
function untilLabel(iso: string, t: Translate): string {
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000)
  if (days < 0) return t('billing.until.overdue')
  if (days === 0) return t('billing.until.today')
  if (days === 1) return t('billing.until.tomorrow')
  if (days < 45) return t('billing.until.days', { days })
  const months = Math.round(days / 30)
  return months === 1 ? t('billing.until.month') : t('billing.until.months', { months })
}

function cardLabel(card: BillingCard, t: Translate): string {
  const brand = card.brand.charAt(0).toUpperCase() + card.brand.slice(1)
  return t('billing.cardLabel', { brand, last4: card.last4 })
}

/**
 * Una tarjeta caducada no se ve hasta que falla un cobro, y entonces ya falló.
 * Con mes y año basta para avisarlo antes.
 */
function cardExpiry(card: BillingCard): 'expired' | 'soon' | 'ok' {
  const now = new Date()
  const months = (card.expYear - now.getFullYear()) * 12 + (card.expMonth - (now.getMonth() + 1))
  if (months < 0) return 'expired'
  if (months <= 1) return 'soon'
  return 'ok'
}


type UpcomingCharge = {
  id: string
  kind: 'membership' | 'domain'
  title: string
  meta: string
  date: string
  amountCents: number | null
  currency: string
  /** El importe sale del cobro anterior, no de una factura emitida. */
  estimated?: boolean
  /**
   * A qué proyecto pertenece, cuando es un dominio.
   *
   * El proyecto no es decoración: es a dónde hay que ir para hacer algo con él.
   * Lo llevaba la tarjeta de Dominios que se quitó, y sin esto la fusión habría
   * perdido esa salida por el camino.
   */
  projectId?: string
}

/**
 * Todo lo que está programado, en una sola lista y por fecha.
 *
 * Antes había que mirar tres bloques y ordenar de cabeza para saber qué se
 * cobra primero. La membresía y cada dominio tienen su propio aniversario —el
 * registro no se puede prorratear—, así que la única vista útil es la mezcla.
 */
function upcomingCharges(data: BillingOverview, t: Translate): UpcomingCharge[] {
  const list: UpcomingCharge[] = []
  const { membership } = data
  const membershipDate = membership.nextChargeAt ?? membership.renewsAt

  if (membership.plan !== 'free' && membership.billable && !membership.cancelAtPeriodEnd && membershipDate) {
    const interval = membership.interval === 'year' ? t('billing.interval.year') : membership.interval === 'month' ? t('billing.interval.month') : null
    list.push({
      id: 'membership',
      kind: 'membership',
      title: interval
        ? t('billing.upcoming.membershipInterval', { plan: PLAN_NAME[membership.plan], interval })
        : t('billing.upcoming.membership', { plan: PLAN_NAME[membership.plan] }),
      meta: t('billing.upcoming.renewal'),
      date: membershipDate,
      amountCents: membership.nextChargeCents ?? membership.amountCents,
      currency: membership.currency,
    })
  }

  return list.sort((a, b) => a.date.localeCompare(b.date))
}

/** Un aviso de la parte de arriba: lo que hay que atender antes que nada. */
/* `info` para lo que sólo explica y no pide nada: un plan otorgado no es un
   problema, y pintarlo en ámbar lo convertiría en uno. */
type Alert = { id: string; tone: 'info' | 'warn' | 'danger'; text: string }

function buildAlerts(data: BillingOverview, t: Translate): Alert[] {
  const alerts: Alert[] = []
  const statusNote = STATUS_NOTE_KEY[data.membership.status]
  if (statusNote) alerts.push({ id: 'status', tone: 'danger', text: t(statusNote) })

  const defaultCard = data.cards.find((card) => card.isDefault)
  if (defaultCard) {
    const expiry = cardExpiry(defaultCard)
    if (expiry === 'expired') {
      alerts.push({
        id: 'card-expired',
        tone: 'danger',
        text: t('billing.alert.cardExpired', { card: cardLabel(defaultCard, t) }),
      })
    } else if (expiry === 'soon') {
      alerts.push({
        id: 'card-soon',
        tone: 'warn',
        text: t('billing.alert.cardSoon', { card: cardLabel(defaultCard, t) }),
      })
    }
  }

  /* Las dos explicaciones que vivían en la tarjeta de membresía. Son avisos
     sobre el estado de la cuenta —lo que está pasando y qué significa—, así que
     su sitio es aquí arriba con los demás y no colgando de un importe. */
  const granted = data.membership.plan !== 'free' && !data.membership.billable
  if (granted) {
    alerts.push({
      id: 'membership-granted',
      tone: 'info',
      text: t('billing.alert.granted'),
    })
  } else if (data.membership.cancelAtPeriodEnd) {
    alerts.push({
      id: 'membership-cancelled',
      tone: 'warn',
      text: t('billing.alert.cancelled', { date: shortDate(data.membership.renewsAt) }),
    })
  }

  const failed = data.domains.filter((domain) => domain.status === 'error')
  for (const domain of failed) {
    alerts.push({
      id: `domain-${domain.id}`,
      tone: 'warn',
      text: t('billing.alert.domainFailed', { hostname: domain.hostname }),
    })
  }

  return alerts
}

/** El cristal de los widgets, en la escala que usa cada sitio de esta pantalla. */
function GlassIcon({ tone, size = 'head', children }: { tone: string; size?: 'head' | 'tile' | 'row'; children: ReactNode }) {
  const scale = size === 'tile' ? 'dash-billing-tile-icon' : size === 'row' ? 'dash-billing-row-icon' : 'dash-settings-head-icon'
  return (
    <span className={`dash-widget-glass-icon dash-sidebar-glass-icon ${scale} is-${tone}`} aria-hidden="true">
      {children}
    </span>
  )
}

function Pill({ tone, children }: { tone: 'ok' | 'warn' | 'muted'; children: ReactNode }) {
  return <em className={`dash-billing-pill is-${tone}`}>{children}</em>
}

function domainStatus(status: string): { label: string; tone: 'ok' | 'warn' | 'muted' } {
  if (status === 'active') return { label: 'billing.domain.active', tone: 'ok' }
  if (status === 'error') return { label: 'billing.domain.error', tone: 'warn' }
  if (status === 'disconnected') return { label: 'billing.domain.disconnected', tone: 'muted' }
  if (status === 'purchasing') return { label: 'billing.domain.purchasing', tone: 'muted' }
  return { label: 'billing.domain.pending', tone: 'muted' }
}

/** Stripe describe la suscripción como “1 × producto (at precio / periodo)”.
 * El importe y la periodicidad ya viven en sus propias columnas, así que aquí
 * sólo hace falta decir qué membresía fue. */
function invoiceConcept(description: string, t: Translate): string {
  const membership = description.match(/^\s*\d+\s*[×x]\s+(.+?)(?:\s+\(at\s+.+\))?\s*$/i)
  if (!membership) {
    const hostname = description.trim()
    const isDomain = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i.test(hostname)
    return isDomain ? t('billing.upcoming.domain', { hostname }) : description
  }

  const interval = /\/\s*year\)\s*$/i.test(description)
    ? t('billing.interval.year')
    : /\/\s*month\)\s*$/i.test(description)
      ? t('billing.interval.month')
      : null
  const plan = membership[1] ?? ''
  return interval
    ? t('billing.upcoming.membershipInterval', { plan, interval })
    : t('billing.upcoming.membership', { plan })
}

type Busy = { kind: 'default' | 'remove' | 'domain'; id: string } | { kind: 'membership' } | null

export function BillingLedger({
  token,
  onOpenPlans,
  onOpenDomains,
  onOpenProject,
}: {
  token: string
  onOpenPlans?: () => void
  /** Abre la administración de dominios del proyecto actualmente abierto. */
  onOpenDomains?: (() => void) | undefined
  /** Abre los ajustes de publicación del proyecto dueño de un dominio. */
  onOpenProject?: ((projectId: string) => void) | undefined
}) {
  const { t } = useI18n()
  const [data, setData] = useState<BillingOverview | null>(null)
  const [loading, setLoading] = useState(true)
  /* Sigue visible mientras se despide, aunque los datos ya hayan llegado. */
  const billingLoader = useRecordLoader(loading)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState<Busy>(null)
  /* El dominio cuyo panel está abierto. Null = ninguno. */
  /* Qué fila tiene su menú abierto. Uno a la vez: dos menús abiertos en una
     lista se solapan y no se sabe cuál manda. */
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null)

  useEffect(() => {
    if (!menuOpenId) return
    const close = (event: Event) => {
      const target = event.target as HTMLElement | null
      if (!target?.closest?.('.dash-project-row-menu-wrap')) setMenuOpenId(null)
    }
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setMenuOpenId(null) }
    document.addEventListener('pointerdown', close)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('pointerdown', close); document.removeEventListener('keydown', onKey) }
  }, [menuOpenId])
  const [addingCard, setAddingCard] = useState(false)
  const [confirm, setConfirm] = useState<
    | { kind: 'cancel' }
    | { kind: 'cancelDomain'; domain: BillingDomainCharge }
    | { kind: 'removeCard'; card: BillingCard }
    | null
  >(null)
  const [portalLoading, setPortalLoading] = useState(false)
  /* Las páginas ya traídas, en orden. Volver atrás no vuelve a pedir nada:
     Stripe sólo sabe avanzar —su cursor es «lo que viene después de esta
     factura»— y lo ya visto es lo único que permite retroceder. */
  const [invoicePages, setInvoicePages] = useState<BillingInvoicePage[]>([])
  const [invoicePage, setInvoicePage] = useState(0)
  const [invoicesLoading, setInvoicesLoading] = useState(false)
  const [invoicesError, setInvoicesError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setData(await getBillingOverview(token))
      setError(null)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('billing.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [token, t])

  useEffect(() => { void load() }, [load])

  /* Toda acción termina releyendo de Stripe en vez de tocar el estado local:
     cambiar la tarjeta predeterminada mueve dos objetos allá y cancelar mueve
     fechas; adivinar el resultado aquí es cómo se desincronizan las pantallas. */
  const run = async (busyState: Busy, action: () => Promise<unknown>, done: string) => {
    if (busy) return
    setBusy(busyState)
    setError(null)
    setNotice(null)
    try {
      await action()
      await load()
      setNotice(done)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('billing.actionFailed'))
    } finally {
      setBusy(null)
    }
  }

  const handleRefresh = async () => {
    if (refreshing) return
    setRefreshing(true)
    setNotice(null)
    await load()
    setRefreshing(false)
  }

  const handlePortal = async () => {
    if (portalLoading) return
    setPortalLoading(true)
    try {
      const { url } = await openBillingPortal(token)
      window.location.href = url
    } catch (failure) {
      /* Lo que diga el servidor: los fallos de aquí son de configuración de
         Stripe y ninguno se arregla reintentando. */
      setError(errorMessage(failure))
      setPortalLoading(false)
    }
  }

  /* La primera página viaja dentro del overview, así que al abrir no hace falta
     una segunda llamada. Cualquier recarga vuelve a empezar por ella: si algo
     cambió en la cuenta, las páginas siguientes que hubiera en memoria ya no
     son necesariamente las que vienen detrás. */
  useEffect(() => {
    if (!data) return
    setInvoicePages([{ invoices: data.invoices, hasMore: data.invoicesHasMore, nextCursor: data.invoicesCursor }])
    setInvoicePage(0)
    setInvoicesError(null)
  }, [data])

  const currentInvoicePage = invoicePages[invoicePage]

  const goToNextInvoicePage = async () => {
    if (invoicesLoading) return
    // Ya traída: sólo hay que moverse.
    if (invoicePages[invoicePage + 1]) { setInvoicePage(invoicePage + 1); return }
    const cursor = currentInvoicePage?.nextCursor
    if (!cursor) return

    setInvoicesLoading(true)
    setInvoicesError(null)
    try {
      const next = await listBillingInvoices(token, cursor)
      setInvoicePages((pages) => [...pages, next])
      setInvoicePage((page) => page + 1)
    } catch (cause) {
      setInvoicesError(cause instanceof Error ? cause.message : t('billing.historyFailed'))
    } finally {
      setInvoicesLoading(false)
    }
  }

  /* La espera sustituye a la pantalla ENTERA, cabecera incluida: un título sobre
     un vacío es una pantalla a medio hacer. Ver `SectionLoader`. */
  if (billingLoader.visible) return <SectionLoader loader={billingLoader} label={t('billing.loading')} />

  if (!data) {
    return (
      <div className="dash-dashboard-view dash-settings-view dash-workspace-billing-page dash-billing-ledger">
        <div className="dash-settings-layout dash-billing-layout">
          <header className="dash-settings-intro dash-billing-intro">
            <DashboardPageHeader title={t('billing.title')} subtitle={t('billing.subtitleShort')} />
          </header>
          <div className="dash-settings-content">
            <div className="dash-billing-content">
              <p className="dash-card-description" role="alert" style={{ margin: 0, color: '#ef4444' }}>{error ?? t('billing.loadFailed')}</p>
              <button type="button" className="dash-secondary-btn" onClick={() => void load()} style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <IconRefresh size={14} /> {t('billing.retry')}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const { membership, domains, cards } = data
  const isPaid = membership.plan !== 'free'
  /* Un plan que Stripe no conoce: existe en tu cuenta pero nadie lo cobra. La
     pantalla lo dice y esconde lo que no puede funcionar, en vez de enseñar un
     importe vacío y un botón de cancelar que reventaría contra Stripe. */
  const granted = isPaid && !membership.billable
  const pageInvoices = currentInvoicePage?.invoices ?? []
  /* Que haya historial lo decide la primera página, no la que se esté mirando:
     al avanzar, una página intermedia podría quedarse sin filas visibles si
     todas fueran borradores. */
  const hasInvoices = (invoicePages[0]?.invoices.length ?? 0) > 0
  const upcoming = upcomingCharges(data, t)
  const alerts = buildAlerts(data, t)
  /* La cancelación explica el estado de la tarjeta de Membresía y vive junto a
     ella. Los demás avisos siguen arriba porque afectan a la cuenta completa. */
  const membershipAlert = alerts.find((alert) => alert.id === 'membership-cancelled')
  const accountAlerts = alerts.filter((alert) => alert.id !== 'membership-cancelled')

  return (
    <div className="dash-dashboard-view dash-settings-view dash-workspace-billing-page dash-billing-ledger">
      <div className="dash-settings-layout dash-billing-layout">
        <header className="dash-settings-intro dash-billing-intro">
          <DashboardPageHeader
            title={t('billing.title')}
            subtitle={t('billing.subtitle')}
            actions={
              <>
                <button type="button" className="dash-secondary-btn" onClick={() => void handleRefresh()} disabled={refreshing}>
                  {refreshing ? <IconLoader2 size={14} className="dash-spin" /> : <IconRefresh size={14} />} {t('billing.refresh')}
                </button>
                {data.hasCustomer ? (
                  <button type="button" className="dash-secondary-btn" onClick={() => void handlePortal()} disabled={portalLoading}>
                    {portalLoading ? <IconLoader2 size={14} className="dash-spin" /> : <IconExternalLink size={14} />} {t('billing.stripePortal')}
                  </button>
                ) : null}
                {onOpenPlans ? (
                  <button type="button" className="dash-primary-btn" onClick={onOpenPlans}>
                    {t(isPaid ? 'billing.changePlan' : 'billing.seePlans')} <IconArrowRight size={14} />
                  </button>
                ) : null}
              </>
            }
          />
        </header>

        <div className="dash-settings-content">
          <div className="dash-billing-content">
        {error ? <p className="dash-card-description" role="alert" style={{ margin: 0, color: '#ef4444' }}>{error}</p> : null}
        {notice ? <p className="dash-card-description" role="status" style={{ margin: 0, color: '#10b981' }}>{notice}</p> : null}

        {accountAlerts.length > 0 ? (
          <div className="dash-billing-alerts">
            {accountAlerts.map((alert) => (
              <div className={`dash-billing-alert is-${alert.tone}`} key={alert.id} role="status">
                {alert.tone === 'danger'
                  ? <IconAlertCircle size={17} />
                  : alert.tone === 'info'
                    ? <IconInfoCircle size={17} />
                    : <IconAlertTriangle size={17} />}
                <p>{alert.text}</p>
              </div>
            ))}
          </div>
        ) : null}

        {/* Membresía y dominios son productos distintos. Cada uno tiene su
            propio contenedor y, dentro, una fila por elemento con su acción. */}
        <div className="dash-billing-products-grid">
          <section className="dash-settings-card dash-billing-widget is-membership">
            <div className="dash-settings-card-head">
              <GlassIcon tone="graphite"><IconRosetteDiscountCheck size={18} /></GlassIcon>
              <div className="dash-settings-card-head-copy">
                <h3>{t('billing.membership.title')}</h3>
                <p>{t('billing.membership.subtitle')}</p>
              </div>
            </div>

            <div className="dash-billing-panel">
              <article className="dash-billing-membership-card">
                <svg className="dash-billing-membership-mascot" viewBox="0 0 120 120" width="84" height="84" aria-hidden="true" focusable="false">
                  <path className="dash-billing-membership-mascot-outline" d={MASCOT_OUTLINE_PATH} />
                  <path className="dash-billing-membership-mascot-eye" d="M39.6 70.8 L39.6 54.6 A6.6 6.6 0 0 1 52.8 54.6 L52.8 70.8 A8.76 8.76 0 0 0 39.6 70.8 Z" />
                  <path className="dash-billing-membership-mascot-eye" d="M67.2 70.8 L67.2 54.6 A6.6 6.6 0 0 1 80.4 54.6 L80.4 70.8 A8.76 8.76 0 0 0 67.2 70.8 Z" />
                </svg>

                <div className="dash-billing-membership-topline">
                  <span className="dash-billing-membership-eyebrow">{t('billing.membership.eyebrow')}</span>
                  {isPaid ? (granted
                    ? <Pill tone="muted">{t('billing.membership.noCharge')}</Pill>
                    : membership.cancelAtPeriodEnd
                      ? <Pill tone="warn">{t('billing.membership.cancelled')}</Pill>
                      : <Pill tone="ok">{t('billing.membership.active')}</Pill>) : <Pill tone="muted">{t('billing.membership.starter')}</Pill>}
                </div>

                <div className="dash-billing-membership-main">
                  <h4>{t('billing.membership.plan', { plan: PLAN_NAME[membership.plan] })}</h4>
                  <div className="dash-billing-membership-price">
                    <strong>
                      {granted
                        ? t('billing.membership.noCharge')
                        : isPaid
                          ? money(membership.amountCents, membership.currency)
                          : t('billing.membership.free')}
                    </strong>
                    {!granted && isPaid ? <span>{t(membership.interval === 'year' ? 'billing.membership.perYear' : 'billing.membership.perMonth')}</span> : null}
                  </div>
                  <p>
                    {granted
                      ? t('billing.membership.grantedUntil', { date: shortDate(membership.renewsAt) })
                      : isPaid
                        ? t(membership.cancelAtPeriodEnd ? 'billing.membership.endsOn' : 'billing.membership.renewsOn', { date: shortDate(membership.renewsAt) })
                        : t('billing.membership.freeBody')}
                  </p>
                </div>

                {!granted && (isPaid || onOpenPlans) ? (
                  <footer className="dash-billing-membership-actions">
                  {isPaid ? (
                    <>
                      {onOpenPlans ? (
                        <button type="button" className="dash-billing-membership-primary" onClick={onOpenPlans}>{t('billing.membership.change')}</button>
                      ) : null}
                      {membership.cancelAtPeriodEnd ? (
                        <button
                          type="button"
                          className="dash-billing-membership-primary"
                          disabled={busy?.kind === 'membership'}
                          onClick={() => void run({ kind: 'membership' }, () => resumeMembership(token), t('billing.membership.resumed'))}
                        >
                          {busy?.kind === 'membership' ? <IconLoader2 size={14} className="dash-spin" /> : t('billing.membership.resume')}
                        </button>
                      ) : (
                        <button type="button" className="dash-billing-membership-cancel" onClick={() => setConfirm({ kind: 'cancel' })}>{t('billing.membership.cancel')}</button>
                      )}
                    </>
                  ) : onOpenPlans ? (
                    <button type="button" className="dash-billing-membership-primary" onClick={onOpenPlans}>{t('billing.seePlans')} <IconArrowRight size={14} /></button>
                  ) : null}
                  </footer>
                ) : null}
              </article>

              {membershipAlert ? (
                <div className="dash-billing-alert is-warn dash-billing-membership-alert" role="status">
                  <IconAlertTriangle size={17} aria-hidden="true" />
                  <p>{membershipAlert.text}</p>
                </div>
              ) : null}
            </div>
          </section>

          <section className="dash-settings-card dash-billing-widget is-domains">
            <div className="dash-settings-card-head">
              <GlassIcon tone="graphite"><IconWorld size={18} /></GlassIcon>
              <div className="dash-settings-card-head-copy">
                <h3>{t('billing.domains.title')}</h3>
                <p>{t('billing.domains.subtitle')}</p>
              </div>
              {onOpenDomains ? (
                <div className="dash-billing-head-actions">
                  <button type="button" className="dash-secondary-btn" onClick={onOpenDomains}>
                    {t('billing.domains.view')} <IconArrowRight size={14} />
                  </button>
                </div>
              ) : null}
            </div>

            <div className="dash-billing-panel">
              {domains.length > 0 ? domains.map((domain) => {
                const status = domainStatus(domain.status)
                const isCancelling = busy?.kind === 'domain' && busy.id === domain.id
                return (
                  <div className="dash-billing-box dash-billing-list-row dash-billing-product-row" key={domain.id}>
                    <GlassIcon tone="graphite" size="row"><IconWorld size={15} /></GlassIcon>
                    <span className="dash-billing-list-copy">
                      <strong>{domain.hostname}<Pill tone={status.tone}>{t(status.label)}</Pill></strong>
                      <small>
                        {domain.projectName}
                        {domain.renewsAt ? ` · vigente hasta el ${shortDate(domain.renewsAt)}` : ''}
                      </small>
                    </span>
                    <span className="dash-billing-list-side">
                      {domain.status === 'purchasing' ? (
                        <button type="button" className="dash-billing-text-action" disabled>{t('billing.domains.registering')}</button>
                      ) : (
                        /*
                         * Cancelar, detrás de los tres puntos.
                         *
                         * Estaba suelto al final de la fila, al alcance del
                         * mismo dedo que va a «Administrar», y cancelar un
                         * dominio no se deshace: se pierde el año pagado. Una
                         * acción irreversible no puede vivir a un clic de
                         * distancia en una lista que se recorre mirando fechas.
                         *
                         * Y es lo único que queda aquí: administrar el dominio
                         * —conectarlo a otro sitio, tocar su DNS— se mudó a la
                         * pantalla de Dominios, que es donde tiene sentido.
                         * Facturación responde qué se cobra y cuándo.
                         */
                        <div className={`dash-project-row-menu-wrap${menuOpenId === domain.id ? ' is-open' : ''}`}>
                          <button
                            type="button"
                            className="project-menu-btn dash-icon-btn dash-page-options-btn"
                            aria-label={t('common.optionsOf', { name: domain.hostname })}
                            data-tooltip={t('billing.options')}
                            aria-expanded={menuOpenId === domain.id}
                            disabled={busy !== null}
                            onClick={() => { setMenuOpenId(menuOpenId === domain.id ? null : domain.id) }}
                          >
                            <IconDots size={16} />
                          </button>
                          {menuOpenId === domain.id ? (
                            <div className="project-menu dash-project-row-menu" role="menu">
                              {onOpenDomains ? (
                                <button
                                  type="button"
                                  role="menuitem"
                                  className="project-menu-item"
                                  onClick={() => { setMenuOpenId(null); onOpenDomains() }}
                                >
                                  <IconSettings size={14} /> {t('billing.domains.manage')}
                                </button>
                              ) : null}
                              <button
                                type="button"
                                role="menuitem"
                                className="project-menu-item danger"
                                onClick={() => { setMenuOpenId(null); setConfirm({ kind: 'cancelDomain', domain }) }}
                              >
                                <IconX size={14} /> {t(isCancelling ? 'billing.membership.cancelling' : 'billing.membership.cancelRenewal')}
                              </button>
                            </div>
                          ) : null}
                        </div>
                      )}
                    </span>
                  </div>
                )
              }) : (
                <div className="dash-billing-empty">
                  <p className="dash-card-description" style={{ margin: 0 }}>{t('billing.domains.empty')}</p>
                  {onOpenDomains ? (
                    <button type="button" className="dash-secondary-btn" onClick={onOpenDomains}>{t('billing.domains.search')} <IconArrowRight size={14} /></button>
                  ) : null}
                </div>
              )}
            </div>
          </section>
        </div>

        <div className="dash-billing-widget-grid">
        {/* La tarjeta «Membresía» estaba aquí y repetía plan, importe y fecha
            de renovación —los mismos que el mosaico de arriba— para poder
            colgarles dos botones. Los botones subieron al mosaico y las dos
            explicaciones que llevaba al pie pasaron a los avisos, que es donde
            va lo que le está pasando a la cuenta. */}
        {/* ── Próximos cobros ───────────────────────────────────────────── */}
          <section className="dash-settings-card dash-billing-widget is-upcoming">
            <div className="dash-settings-card-head">
              <GlassIcon tone="graphite"><IconCalendarDollar size={18} /></GlassIcon>
              <div className="dash-settings-card-head-copy">
                <h3>{t('billing.upcomingTitle')}</h3>
                <p>{t('billing.upcomingSubtitle')}</p>
              </div>
            </div>

            <div className="dash-billing-panel">
              {upcoming.length > 0 ? (
                <>
                  {upcoming.map((charge) => {
                    /* La fila de un dominio lleva a su proyecto; la de la
                       membresía no lleva a ninguna parte, así que no se pinta
                       como si se pudiera pulsar. */
                    const goToProject = charge.projectId && onOpenProject
                      ? () => onOpenProject(charge.projectId!)
                      : null
                    return (
                    <article
                      className={`dash-billing-box dash-billing-invoice-row dash-billing-upcoming-row${goToProject ? ' is-clickable' : ''}`}
                      key={`${charge.kind}-${charge.id}`}
                      {...(goToProject
                        ? {
                            role: 'button',
                            tabIndex: 0,
                            onClick: goToProject,
                            onKeyDown: (event: React.KeyboardEvent) => {
                              if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); goToProject() }
                            },
                          }
                        : {})}
                    >
                      <div className="dash-billing-invoice-head">
                        <strong>{charge.title}</strong>
                      </div>
                      <div className="dash-billing-invoice-meta">{charge.meta}</div>
                      <div className="dash-billing-invoice-foot">
                        <span className="dash-billing-invoice-total">
                          <strong className="dash-billing-list-amount">
                            {money(charge.amountCents, charge.currency)}
                          </strong>
                          <Pill tone="muted">{untilLabel(charge.date, t)}</Pill>
                        </span>
                        <time className="dash-billing-invoice-date" dateTime={charge.date}>{shortDate(charge.date)}</time>
                      </div>
                    </article>
                    )
                  })}
                  {upcoming.some((charge) => charge.estimated) ? (
                    <p className="dash-billing-note">
                      <IconAlertCircle size={13} />
                      <span>{t('billing.upcomingNote')}</span>
                    </p>
                  ) : null}
                </>
              ) : (
                <div className="dash-billing-empty">
                  <p className="dash-card-description" style={{ margin: 0 }}>
                    {t('billing.noUpcoming')}
                  </p>
                </div>
              )}
            </div>
          </section>

        {/* ── Dominios ──────────────────────────────────────────────────── */}
        {/* La tarjeta «Dominios» estaba aquí y era una tercera copia de lo
            mismo. Sus dos contenidos ya vivían en otro sitio con mejor motivo:
            las renovaciones son cobros futuros y salen en «Próximos cobros»
            —`upcomingCharges` ya las mete—, y un registro fallido no es un
            cobro sino algo que hay que resolver, así que sale arriba con los
            demás avisos. Lo único que aportaba era un contenedor más. */}
        {/* ── Facturas ──────────────────────────────────────────────────── */}
        <section className="dash-settings-card dash-billing-widget is-history">
          <div className="dash-settings-card-head">
            <GlassIcon tone="graphite"><IconReceipt size={18} /></GlassIcon>
            <div className="dash-settings-card-head-copy">
              <h3>{t('billing.historyTitle')}</h3>
              <p>{t('billing.historySubtitle')}</p>
            </div>
          </div>

          <div className="dash-billing-panel">
            {hasInvoices ? (
              <>
                {pageInvoices.map((invoice) => {
                  /* La etiqueta es una CLAVE: se traduce aquí. Un estado que no
                     conocemos sale tal cual lo dice Stripe, sin traducir. */
                  const known = INVOICE_STATUS[invoice.status]
                  const status = known
                    ? { label: t(known.label), tone: known.tone }
                    : { label: invoice.status, tone: 'muted' as const }
                  return (
                    <article className="dash-billing-box dash-billing-invoice-row" key={invoice.id}>
                      <div className="dash-billing-invoice-head">
                        <strong>{invoiceConcept(invoice.description, t)}</strong>
                      </div>
                      <div className="dash-billing-invoice-meta">
                        {invoice.number ? t('billing.invoice.number', { number: invoice.number }) : null}
                      </div>
                      <div className="dash-billing-invoice-foot">
                        <span className="dash-billing-invoice-total">
                          <strong className="dash-billing-list-amount">{money(invoice.amountCents, invoice.currency)}</strong>
                          <Pill tone={status.tone}>{status.label}</Pill>
                        </span>
                        <time className="dash-billing-invoice-date" dateTime={invoice.createdAt}>{shortDate(invoice.createdAt)}</time>
                        {invoice.hostedUrl || invoice.pdfUrl ? (
                          <span className="dash-billing-invoice-actions">
                            {invoice.hostedUrl ? (
                              <a
                                className="dash-billing-icon-link"
                                href={invoice.hostedUrl}
                                target="_blank"
                                rel="noreferrer"
                                title={t('billing.viewInvoice')}
                                aria-label={t('billing.invoice.viewOf', { date: shortDate(invoice.createdAt) })}
                              >
                                <IconExternalLink size={16} />
                              </a>
                            ) : null}
                            {invoice.pdfUrl ? (
                              <a
                                className="dash-billing-icon-link"
                                href={invoice.pdfUrl}
                                target="_blank"
                                rel="noreferrer"
                                title={t('billing.downloadPdf')}
                                aria-label={t('billing.invoice.downloadOf', { date: shortDate(invoice.createdAt) })}
                              >
                                <IconDownload size={16} />
                              </a>
                            ) : null}
                          </span>
                        ) : null}
                      </div>
                    </article>
                  )
                })}

                {invoicesError ? (
                  <p className="dash-billing-note is-warn" role="alert">
                    <IconAlertCircle size={13} />
                    <span>{invoicesError}</span>
                  </p>
                ) : null}

                {/* Stripe pagina por cursor, así que no sabe —ni puede decir—
                    cuántas páginas hay: se numera la actual y se ofrece avanzar
                    mientras queden. Es como pagina su propio panel. */}
                {invoicePage > 0 || currentInvoicePage?.hasMore ? (
                  <nav className="dash-billing-pager" aria-label={t('billing.pagerAria')}>
                    <button
                      type="button"
                      className="dash-secondary-btn"
                      disabled={invoicePage === 0 || invoicesLoading}
                      onClick={() => setInvoicePage((page) => Math.max(0, page - 1))}
                    >
                      <IconChevronLeft size={14} /> {t('billing.pagerPrevious')}
                    </button>
                    <span className="dash-billing-pager-label">{t('billing.pagerPage', { page: invoicePage + 1 })}</span>
                    <button
                      type="button"
                      className="dash-secondary-btn"
                      disabled={!currentInvoicePage?.hasMore || invoicesLoading}
                      onClick={() => void goToNextInvoicePage()}
                    >
                      {invoicesLoading ? <IconLoader2 size={14} className="dash-spin" /> : null}
                      {t('billing.pagerNext')} <IconChevronRight size={14} />
                    </button>
                  </nav>
                ) : null}

                {/* El portal de Stripe queda como escape para lo que no vive
                    aquí: los datos fiscales, los recibos y los medios de pago
                    de cada cobro. */}
                {data.hasCustomer ? (
                  <p className="dash-billing-note">
                    <IconAlertCircle size={13} />
                    <span>
                      {t('billing.portalNote1')}{' '}
                      <button className="dash-billing-text-action" type="button" onClick={() => void handlePortal()} disabled={portalLoading}>
                        {t(portalLoading ? 'billing.opening' : 'billing.openPortalShort')}
                      </button>
                      {' '}{t('billing.portalNote2')}
                    </span>
                  </p>
                ) : null}
              </>
            ) : (
              <div className="dash-billing-empty">
                <p className="dash-card-description" style={{ margin: 0 }}>
                  {granted
                    ? t('billing.noInvoicesGranted')
                    : t('billing.noInvoices')}
                </p>
                {data.hasCustomer ? (
                  <button className="dash-secondary-btn" type="button" onClick={() => void handlePortal()} disabled={portalLoading}>
                    {portalLoading ? t('billing.opening') : <>{t('billing.openPortal')} <IconExternalLink size={14} /></>}
                  </button>
                ) : null}
              </div>
            )}
          </div>
        </section>

        <section className="dash-settings-card dash-billing-widget is-payment-methods">
          <div className="dash-settings-card-head">
            <GlassIcon tone="graphite"><IconCreditCard size={18} /></GlassIcon>
            <div className="dash-settings-card-head-copy">
              <h3>{t('billing.cards.title')}</h3>
              <p>{t('billing.cards.subtitle')}</p>
            </div>
            {data.hasCustomer ? (
              <div className="dash-billing-head-actions">
                <button className="dash-secondary-btn" type="button" onClick={() => setAddingCard(true)}>
                  <IconPlus size={14} /> {t('billing.cards.add')}
                </button>
              </div>
            ) : null}
          </div>

          <div className="dash-billing-panel">
            {cards.length > 0 ? (
              <div className="dash-billing-payment-grid">
                {cards.map((card) => {
                  const expiry = cardExpiry(card)
                  return (
                    <article className={`dash-billing-box dash-billing-payment-card${card.isDefault ? ' is-default' : ''}${expiry === 'expired' ? ' is-expired' : ''}`} key={card.id}>
                      <div className="dash-billing-payment-card-head">
                        <CardMark brand={card.brand} />
                        <span className="dash-billing-payment-card-badges">
                          {card.isDefault ? <Pill tone="ok">{t('billing.cards.default')}</Pill> : null}
                          {expiry === 'expired'
                            ? <Pill tone="warn">{t('billing.cards.expired')}</Pill>
                            : expiry === 'soon'
                              ? <Pill tone="warn">{t('billing.cards.expiringSoon')}</Pill>
                              : null}
                        </span>
                      </div>
                      <div className="dash-billing-payment-card-number" aria-label={cardLabel(card, t)}>
                        <span aria-hidden="true">••••</span> {card.last4}
                      </div>
                      <div className="dash-billing-payment-card-expiry">
                        <span>{t('billing.cards.expiry')}</span>
                        <strong>{String(card.expMonth).padStart(2, '0')}/{card.expYear}</strong>
                      </div>
                      <footer className="dash-billing-payment-card-actions">
                        {card.isDefault ? null : (
                          <button
                            type="button"
                            className="dash-billing-text-action"
                            disabled={busy !== null}
                            onClick={() => void run({ kind: 'default', id: card.id }, () => setDefaultCard(token, card.id), t('billing.cards.defaultDone'))}
                          >
                            {t(busy?.kind === 'default' && busy.id === card.id ? 'billing.cards.makingDefault' : 'billing.cards.makeDefault')}
                          </button>
                        )}
                        <button
                          type="button"
                          className="dash-billing-text-action is-danger"
                          disabled={busy !== null}
                          onClick={() => setConfirm({ kind: 'removeCard', card })}
                        >
                          {t('billing.cards.remove')}
                        </button>
                      </footer>
                    </article>
                  )
                })}
              </div>
            ) : (
              <div className="dash-billing-empty">
                <p className="dash-card-description" style={{ margin: 0 }}>
                  {data.hasCustomer
                    ? t('billing.cards.empty')
                    : granted
                      ? t('billing.cards.emptyGranted')
                      : t('billing.cards.emptyNoCharges')}
                </p>
                {data.hasCustomer ? (
                  <button className="dash-secondary-btn" type="button" onClick={() => setAddingCard(true)}>
                    <IconPlus size={14} /> {t('billing.cards.addCard')}
                  </button>
                ) : null}
              </div>
            )}
          </div>
        </section>
        </div>
          </div>
        </div>
      </div>

      {addingCard ? (
        <AddCardDialog
          token={token}
          onClose={() => setAddingCard(false)}
          onSaved={() => { setAddingCard(false); setNotice(t('billing.cards.saved')); void load() }}
        />
      ) : null}

      {confirm?.kind === 'cancel' ? (
        <ConfirmDialog
          title={t('billing.membership.cancelTitle')}
          wide
          message={<CancelMembershipSummary token={token} plan={membership.plan} until={shortDate(membership.renewsAt)} />}
          confirmLabel={t('billing.confirm.yesCancel')}
          confirmingLabel={t('billing.confirm.cancelling')}
          cancelLabel={t('billing.membership.keepPlan')}
          tone="danger"
          onConfirm={async () => {
            setConfirm(null)
            await run({ kind: 'membership' }, () => cancelMembership(token), t('billing.membership.cancelled.toast'))
          }}
          onCancel={() => setConfirm(null)}
        />
      ) : null}

      {confirm?.kind === 'removeCard' ? (
        <ConfirmDialog
          title={t('billing.cards.removeTitle')}
          message={t('billing.cards.removeBody', { card: cardLabel(confirm.card, t) })}
          confirmLabel={t('billing.cards.remove')}
          confirmingLabel={t('billing.cards.removing')}
          tone="danger"
          onConfirm={async () => {
            const card = confirm.card
            setConfirm(null)
            await run({ kind: 'remove', id: card.id }, () => removeCard(token, card.id), t('billing.cards.removed'))
          }}
          onCancel={() => setConfirm(null)}
        />
      ) : null}

      {confirm?.kind === 'cancelDomain' ? (
        <ConfirmDialog
          title={t('billing.domain.cancelTitle', { domain: confirm.domain.hostname })}
          message={<>{t('billing.domain.disconnect.body1')} <strong>{confirm.domain.projectName}</strong> {t('billing.domain.disconnect.body2', { date: shortDate(confirm.domain.renewsAt) })}</>}
          confirmLabel={t('billing.confirm.yesCancel')}
          confirmingLabel={t('billing.confirm.cancelling')}
          cancelLabel={t('billing.domain.keep')}
          tone="danger"
          onConfirm={async () => {
            const domain = confirm.domain
            setConfirm(null)
            await run(
              { kind: 'domain', id: domain.id },
              () => deleteProjectDomain(token, domain.projectId, domain.id),
              t('billing.domain.disconnectedToast', { domain: domain.hostname }),
            )
          }}
          onCancel={() => setConfirm(null)}
        />
      ) : null}

    </div>
  )
}
